import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Plane, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const navLinks = [
  { key: "how_it_works", href: "#how-it-works" },
  { key: "pricing", href: "#pricing" },
  { key: "faq", href: "#faq" },
  { key: "country_guides", href: "#country-guides" },
  { key: "my_orders", href: "/my-orders", isRoute: true },
  { key: "support", href: "#footer" },
];

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (link) => {
    setMobileOpen(false);
    if (link.isRoute) {
      navigate(link.href);
      return;
    }
    // If on homepage, scroll to section; otherwise navigate home first
    if (location.pathname === "/") {
      const el = document.querySelector(link.href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + link.href);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-[#e2e8f0]"
          : "bg-white/70 backdrop-blur-md"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 bg-[#1e3a5f] rounded-lg flex items-center justify-center group-hover:bg-[#f97316] transition-colors duration-300">
              <Plane className="w-5 h-5 text-white -rotate-45" />
            </div>
            <span className="font-['Outfit'] font-bold text-lg text-[#1e3a5f] hidden sm:block">
              OnwardFlighTickets
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.key}
                onClick={() => handleNavClick(link)}
                className="px-3 py-2 text-sm text-[#1f2937] hover:text-[#f97316] font-medium transition-colors rounded-lg hover:bg-[#f8fafc]"
                data-testid={`nav-${link.key}`}
              >
                {t(`nav.${link.key}`)}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            <button
              onClick={() => handleNavClick({ href: "#hero" })}
              className="hidden sm:flex items-center gap-2 bg-[#f97316] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#ea580c] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="get-ticket-cta"
            >
              {t("nav.get_ticket")}
            </button>

            {/* Mobile menu */}
            <div className="lg:hidden">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button className="p-2 text-[#1f2937]" data-testid="mobile-menu-trigger">
                    <Menu className="w-6 h-6" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80 bg-white">
                  <SheetTitle className="font-['Outfit'] text-[#1e3a5f]">Menu</SheetTitle>
                  <nav className="flex flex-col gap-1 mt-8">
                    {navLinks.map((link) => (
                      <button
                        key={link.key}
                        onClick={() => handleNavClick(link)}
                        className="px-4 py-3 text-left text-[#1f2937] hover:text-[#f97316] hover:bg-[#f8fafc] rounded-lg font-medium transition-colors"
                        data-testid={`mobile-nav-${link.key}`}
                      >
                        {t(`nav.${link.key}`)}
                      </button>
                    ))}
                    <div className="mt-4 px-4">
                      <LanguageSwitcher />
                    </div>
                    <div className="mt-4 px-4">
                      <button
                        onClick={() => handleNavClick({ href: "#hero" })}
                        className="w-full bg-[#f97316] text-white px-4 py-3 rounded-lg font-medium hover:bg-[#ea580c] transition-colors"
                        data-testid="mobile-get-ticket-cta"
                      >
                        {t("nav.get_ticket")}
                      </button>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
